import React, { ReactNode, useEffect, useState, useCallback } from "react";
import { useOAuth2 } from "@shared/contexts/OAuth2Context";
import ErrorPage from "@pages/ErrorPage";
import { LoadingPage } from "@components/ui";
import { OpenIdService } from "@shared/services";
import { PagePaths } from "@shared/constants";
import { ComposeURLWithGetQueryArguments } from "@shared/utils";
import { useNavigate } from "react-router-dom";

import { jwtVerify, importJWK, JWK, decodeJwt } from "jose";
import { useLocation } from "react-router-dom";
import { ENVIRONMENT } from "@shared/utils/environment";

type RenderOnBoardingProps = { children: ReactNode };

/**
 * Decide what to show before the onboarding page is allowed to render:
 * nothing off-route, a loader while the session handle resolves or the token
 * is verified, an error page without a token, or null to let it through.
 */
function resolveGate(
  isCorrectRoute: boolean,
  isResolvingSession: boolean,
  state: string,
  verifiedCode: boolean | undefined
): React.ReactElement | null {
  if (!isCorrectRoute) return <></>;
  if (isResolvingSession) return <LoadingPage />;
  if (!state) return <ErrorPage missingArguments={["code"]} />;
  if (verifiedCode !== true) return <LoadingPage />;
  return null;
}

export const RenderOnBoarding = ({ children }: RenderOnBoardingProps) => {
  const { state, isResolvingSession } = useOAuth2();
  const [verifiedCode, setVerifiedCode] = useState<boolean | undefined>(undefined);
  const navigate = useNavigate();

  const location = useLocation();

  const currentPage = location.pathname;
  const is_correct_route = currentPage.startsWith("/external/");

  const redirectAfterVerification = useCallback((response: { isValid: boolean; isExpired: boolean }) => {
    if (response.isExpired) {
      console.warn("Onboarding token expired, redirecting to login identity page");
      navigate(ComposeURLWithGetQueryArguments(PagePaths.LOGIN.IDENTITY, { tokenExpired: "true" }));
      return;
    }

    console.error("Onboarding token verification failed (not expired):", response);
    navigate(ComposeURLWithGetQueryArguments(PagePaths.LOGIN.IDENTITY));
  }, [navigate]);

  const initializeVerifyCode = useCallback(async () => {
    const response = await verifyCode(state);
    setVerifiedCode(response.isValid);
    
    if (!response.isValid) {
      redirectAfterVerification(response);
    }
  }, [state, redirectAfterVerification]);

  useEffect(() => {
    const shouldVerify = is_correct_route && !!state;
    if (shouldVerify) initializeVerifyCode();
  }, [is_correct_route, state, initializeVerifyCode]);

  return resolveGate(is_correct_route, isResolvingSession, state, verifiedCode) ?? <>{children}</>;
};

const TOKEN_EXPIRED_PREDICATES: Array<(err: any) => boolean> = [
  (err) => err?.code === 'ERR_JWT_EXPIRED',
  (err) => err?.code === 'ERR_JWT_CLAIM_VALIDATION_FAILED',
  (err) => !!err?.message?.includes('expired'),
  (err) => !!err?.message?.includes('Expired'),
  (err) => !!err?.message?.includes('JWTExpired'),
];

const isTokenExpiredError = (err: any): boolean => {
  return TOKEN_EXPIRED_PREDICATES.some(predicate => predicate(err));
};

const getJwksKey = async (): Promise<JWK> => {
  const jwks_datas = await OpenIdService.getOpenIdjwks();
  if (!jwks_datas?.keys || jwks_datas.keys.length === 0) {
    console.error("No JWKS data available");
    throw new Error("No JWKS data available");
  }
  const rsaKeyPublic = jwks_datas.keys[0];
  return {
    ...rsaKeyPublic,
    n: toBase64Url(rsaKeyPublic.n),
  };
};

const verifyTokenWithKey = async (code: string, key: JWK): Promise<void> => {
  const importedKey = await importJWK(key, "RS256");
  const { payload } = await jwtVerify(code, importedKey);
  console.log("Token is valid:", payload);
};

function getVerificationFailureDetail(err: any): string {
  if (err && err.code) return err.code;
  if (err && err.message) return err.message;
  return 'unknown';
}

export async function verifyCode(code: string): Promise<{ isValid: boolean; isExpired: boolean }> {
  if (process.env.NODE_ENV === 'test') {
    return { isValid: true, isExpired: false };
  }

  try {
    const rsaKeyPublicToBase64Url = await getJwksKey();
    await verifyTokenWithKey(code, rsaKeyPublicToBase64Url);
    return { isValid: true, isExpired: false };
  } catch (err: any) {
    console.error("Token verification failed:", err);
    
    if (ENVIRONMENT === 'local') {
      try {
        decodeJwt(code);
        console.warn("[local] JWKS verification failed but token decodes; accepting for local dev.");
        return { isValid: true, isExpired: false };
      } catch { /* fall through to normal error handling */ }
    }

    if (isTokenExpiredError(err)) {
      console.warn("Token is expired");
      return { isValid: false, isExpired: true };
    }
    
    console.warn("Token verification failed for non-expiration reason:", getVerificationFailureDetail(err));
    return { isValid: false, isExpired: false };
  }
}

function toBase64Url(base64: string): string {
  return base64.replace(/=/g, "").replace(/\+/g, "-").replace(/\//g, "_");
}
